import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useSiteContext, isBitiba } from '@zooplus/commons-lang';
import { SponsoredTag, Rating, AutoshipmentLogoLockup, TextBadge, CartButton, ProductReducedPrice, ProductStandardPrice, ProductPrice } from '@zooplus/ui-components';
import useAdobeUtils from '../../hooks/use-adobe-utils.js';
import { useTranslation } from '../../hooks/use-translations.js';
import styles from './RecommendationsProductCard.module.scss.js';

function convertReferencePriceToRefPrice(referencePrice) {
    if (!referencePrice ||
        !referencePrice.amount ||
        !referencePrice.difference ||
        !referencePrice.label) {
        return undefined;
    }
    return {
        amount: referencePrice.amount,
        difference: referencePrice.difference,
        label: referencePrice.label,
    };
}
const RecommendationsProductCard = ({ className = '', recommendationAttributes, addToCartButton, priceVersion = 'old', references, styleProps, setIsMapRefSet, index, ...otherProps }) => {
    const { titleRefs, subtitleRefs, tagBoxRefs, priceRefs, sponsoredBadgeRefs } = references;
    const { titleStyle, subtitleStyle, tagBoxStyle, priceStyle, sponsoredBadgeStyle } = styleProps || {};
    const { t } = useTranslation();
    const { link, productShopIdentifier, articleId, productName, variantName, image, rating, reviewCount, beforePrice, rawCurrentPriceLabel, currentPriceLabelSecondaryCurrency, currentPriceSecondaryCurrency, currentPriceLabel, currentPrice, unit, beforePriceLabel, beforePriceLabelFull, unitPriceLabel, discountType, discountValue, zooplusChoice, sponsored, tooltip, referencePrice, autoshipment, autoshipmentPrice, locale, priceType, } = recommendationAttributes;
    const { loadingIndicator, setRestoreArticle, onAddToCartClick, showPopover } = addToCartButton ?? {};
    const ref = useRef(null);
    const { site } = useSiteContext();
    /**
     * The following list of IDs is used to determine to display or not the difference
     * between the current price and the previous price based on the following UX team's Figma design:
     * https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=7284-25748&node-type=section&t=3zU4szsH9A0UJsYc-0
     */
    const SHOW_DISCOUNT_SITE_IDS = [
        8, // zooplus.pl
        12, // zooplus.it
    ];
    const { trackAddRecommendationEvent, trackRecommendationClickEvent } = useAdobeUtils();
    const [isLoading, setLoading] = useState({});
    const goToArticleLink = () => {
        trackRecommendationClickEvent(recommendationAttributes);
        if (typeof window !== 'undefined') {
            window.location.href = link;
        }
    };
    const displayPrice = (price, priceVersion) => {
        if (priceVersion == 'old') {
            return tooltip?.text && beforePrice ? (jsx(ProductReducedPrice, { textAlign: { all: 'left' }, pricePrefix: beforePriceLabel ?? undefined, price: price, priceSuffix: unitPriceLabel ?? undefined, tooltip: {
                    id: articleId.toString(),
                    label: 'more info', //ToDo: use a localized phrase
                    text: tooltip.text,
                }, refPrice: convertReferencePriceToRefPrice(referencePrice) })) : (jsx(ProductStandardPrice, { textAlign: { all: 'left' }, price: price, priceSuffix: unitPriceLabel ?? undefined, autoshipmentPrice: autoshipmentObject }));
        }
        const isReducedPrice = priceType !== 'REGULAR' && currentPrice < beforePrice;
        const showPrefixPrice = priceType === 'RETAIL' || priceType === 'SET';
        const showRefPrice = priceType === 'OFFER';
        const showTooltip = showRefPrice && tooltip;
        const showDiscount = SHOW_DISCOUNT_SITE_IDS.includes(site.id);
        const finalRefPrice = {
            ...referencePrice,
            difference: showDiscount ? referencePrice.difference : null,
        };
        return (jsx(ProductPrice, { price: price, tooltip: showTooltip ? { position: 'right-middle', ...tooltip } : undefined, size: "large", refPrice: showRefPrice ? finalRefPrice : undefined, pricePrefix: showPrefixPrice && beforePriceLabelFull !== null ? beforePriceLabelFull : undefined, isReducedPrice: isReducedPrice, previousPriceNewLine: true, autoshipmentPrice: autoshipmentObject, priceSuffix: pricePerUnit }));
    };
    const toggleLoader = useCallback((value) => {
        setLoading((ids) => ({
            ...ids,
            [articleId]: value,
        }));
    }, [articleId]);
    useEffect(() => {
        if (loadingIndicator !== undefined &&
            setRestoreArticle !== undefined &&
            onAddToCartClick !== undefined) {
            !loadingIndicator && toggleLoader(loadingIndicator);
        }
    }, [loadingIndicator, setRestoreArticle, onAddToCartClick, toggleLoader]);
    const addRecommendationToCart = () => {
        if (!onAddToCartClick || !setRestoreArticle) {
            return;
        }
        trackAddRecommendationEvent(recommendationAttributes);
        const data = {
            articleId: articleId,
            quantity: 1,
            voucherApplied: discountType === 'abd',
            ...(ref?.current && showPopover
                ? {
                    popover: {
                        trigger: ref?.current,
                    },
                }
                : {}),
        };
        console.log(`addRecommendationToCart:`);
        console.log(data);
        onAddToCartClick(data);
        toggleLoader(true);
        setRestoreArticle({});
    };
    const setRef = useCallback((map, index) => (node) => {
        if (node) {
            map.current.set(index, node);
            setIsMapRefSet(true);
        }
    }, [setIsMapRefSet]);
    const discountPct = useMemo(() => {
        return `${discountValue != null ? (discountValue * 100).toFixed(0) : '0'}%`;
    }, [discountValue]);
    const pricePrepend = useMemo(() => {
        return rawCurrentPriceLabel?.replace('{0}', '');
    }, [rawCurrentPriceLabel]);
    const secondaryPrice = useMemo(() => {
        return currentPriceLabelSecondaryCurrency?.replace(pricePrepend ?? '', '');
    }, [currentPriceLabelSecondaryCurrency, pricePrepend]);
    const price = useMemo(() => {
        return currentPriceSecondaryCurrency
            ? `${currentPriceLabel} (${secondaryPrice})`
            : `${currentPriceLabel}`;
    }, [currentPriceSecondaryCurrency, currentPriceLabel, secondaryPrice]);
    const pricePerUnit = useMemo(() => {
        return unitPriceLabel && unit ? `${unitPriceLabel} / ${unit}` : undefined;
    }, [unitPriceLabel, unit]);
    let autoshipmentObject;
    if (autoshipmentPrice != null) {
        autoshipmentObject = {
            amount: autoshipmentPrice,
            hideMainPrice: false,
        };
    }
    return (jsxs("div", { className: `${styles.productCard} ${className}`, ...otherProps, children: [jsxs("a", { href: link, className: styles.productInnerCard, style: { textDecoration: 'none' }, onClick: (e) => {
                    e.preventDefault();
                    goToArticleLink();
                }, children: [jsx("div", { className: styles.wrapperImage, children: jsx("img", { src: image, className: styles.imageLayout, alt: productName }) }), jsx("div", { ref: (node) => setRef(sponsoredBadgeRefs, index)(node), style: sponsoredBadgeStyle, children: sponsored && jsx(SponsoredTag, { children: t('general.sponsored.products') }) }), jsxs("div", { className: styles.wrapperProductInfo, children: [jsx(Rating, { rating: rating ?? 0, ratingLabel: `${rating ?? 0}/5`, count: reviewCount == null ? undefined : reviewCount > 99 ? '99+' : reviewCount, size: "sm" }), jsx("div", { ref: (node) => setRef(titleRefs, index)(node), style: titleStyle, className: styles.wrapperProductName, children: jsx("div", { className: styles.productName, children: productName }) }), jsx("div", { ref: (node) => {
                                    subtitleRefs.current.set(index, node);
                                }, style: subtitleStyle, className: styles.wrapperSubtitle, children: jsx("div", { className: styles.subtitle, children: (variantName ?? '').replace(/^\s*-+\s*/, '') }) })] }), jsxs("div", { ref: (node) => setRef(tagBoxRefs, index)(node), style: tagBoxStyle, className: styles.wrapperTagBox, children: [autoshipment && (jsx(AutoshipmentLogoLockup, { locale: locale.replace('_', '-'), realm: site.realm })), zooplusChoice?.active && (jsx(TextBadge, { variant: "positive", style: {
                                    display: zooplusChoice?.active && zooplusChoice?.label ? 'inline-flex' : 'none',
                                }, label: zooplusChoice?.label ?? '' })), discountType === 'abd' && (jsx(TextBadge, { variant: "positive", style: { display: discountType === 'abd' ? 'inline-flex' : 'none' }, label: t('shop.product.article.discount.relative.label', { 0: discountPct }) }))] }), jsx("div", { ref: (node) => setRef(priceRefs, index)(node), style: priceStyle, className: styles.wrapperPrice, children: displayPrice(price, priceVersion) })] }), jsxs("form", { style: { display: 'none' }, className: "zootopia-reco_info", "data-zta": 'zootopia-reco_info_' + articleId, children: [jsx("input", { name: "link", type: "hidden", value: link }), jsx("input", { name: "article_id", type: "hidden", value: articleId }), jsx("input", { name: "product_id", type: "hidden", value: productShopIdentifier })] }), loadingIndicator !== undefined &&
                setRestoreArticle !== undefined &&
                onAddToCartClick !== undefined ? (jsx(CartButton, { className: styles.addToCardButton, ref: showPopover ? ref : null, isLoading: isLoading[articleId], loadingIconType: isBitiba(site.domain) ? 'spinner' : 'paw', withIcon: true, onClick: addRecommendationToCart, "data-zta": 'zootopia-buy_button_' + articleId, "aria-label": t('cart.overview.add.to.cart.button.ariaLabel') })) : null] }));
};

export { RecommendationsProductCard };
//# sourceMappingURL=RecommendationsProductCard.js.map
